import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type {
  FetchFormEntriesRequest,
  FetchFormEntriesResponse,
  AddSectionToLibraryRequest,
  AddSectionToLibraryResponse,
  GetEntitesFromLibraryRequest,
  GetEntitesFromLibraryResponse,
  DeleteSectionsFromLibraryRequest,
  DeleteSectionsFromLibraryResponse,
  FormUserSettingsControllerRequest,
  FormUserSettingsControllerResponse,
  TaskInspectionAndProgressFormsControllerRequest,
  TaskInspectionAndProgressFormsControllerResponse,
  FormFlowControllerRequest,
  FormFlowControllerResponse,
  FormTypesControllerRequest,
  FormTypesControllerResponse,
  FormControllerRequest,
  FormControllerResponse,
} from "@ServerTypes/forms/forms.schema";

// Each former action-dispatcher is split into dedicated endpoints. These maps route an `action` to
// its endpoint path; an unmapped/absent action falls back to the list endpoint.
const TASK_INSPECTION_ACTION_PATHS: Record<string, string> = {
  update: "/forms/updateTaskInspectionAndProgress",
  save: "/forms/saveTaskInspectionAndProgress",
  delete: "/forms/deleteTaskInspectionAndProgress",
  archive: "/forms/archiveTaskInspectionAndProgress",
};
const FORM_USER_SETTINGS_ACTION_PATHS: Record<string, string> = {
  createForm: "/forms/createFormFromUserSettings",
  deleteFormUserSettings: "/forms/deleteFormUserSettings",
  checkFormUserSettings: "/forms/checkFormUserSettings",
};
const FORM_FLOW_ACTION_PATHS: Record<string, string> = {
  update: "/forms/updateFormFlow",
  save: "/forms/saveFormFlow",
  delete: "/forms/deleteFormFlow",
  addToLibrary: "/forms/addFormFlowToLibrary",
  addFromLibrary: "/forms/addFormFlowFromLibrary",
};
const FORM_TYPES_ACTION_PATHS: Record<string, string> = {
  insert: "/forms/insertFormType",
  update: "/forms/updateFormType",
  delete: "/forms/deleteFormType",
};
const FORM_ACTION_PATHS: Record<string, string> = {
  insert: "/forms/insertForm",
  update: "/forms/updateForm",
  delete: "/forms/deleteForm",
  archive: "/forms/archiveForm",
  updateUsedTemplate: "/forms/updateUsedTemplate",
};

// Task-inspection `action` may be at the top level or nested under `value` (matching the old controller).
export async function taskInspectionAndProgressFormsController<
  T extends Exact<TaskInspectionAndProgressFormsControllerRequest, T>,
>(data: T) {
  const d = data as { action?: string; value?: { action?: string } };
  const action = d.action ?? d.value?.action;
  const path = (action && TASK_INSPECTION_ACTION_PATHS[action]) || "/forms/taskInspectionAndProgressFormsController";
  const response = await axiosWrapper.post<TaskInspectionAndProgressFormsControllerResponse>(
    `${getBackendUrl()}${path}`,
    data,
  );
  return response;
}
export async function formUserSettingsController<T extends Exact<FormUserSettingsControllerRequest, T>>(data: T) {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_USER_SETTINGS_ACTION_PATHS[action]) || "/forms/checkFormUserSettings";
  const response = await axiosWrapper.post<FormUserSettingsControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}

export async function fetchFormEntries<T extends Exact<FetchFormEntriesRequest, T>>(data: T) {
  const response = await axiosWrapper.post<FetchFormEntriesResponse>(`${getBackendUrl()}/forms/fetchFormEntries`, data);
  return response;
}
export async function formFlowController<T extends Exact<FormFlowControllerRequest, T>>(data: T) {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_FLOW_ACTION_PATHS[action]) || "/forms/formFlowController";
  const response = await axiosWrapper.post<FormFlowControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}
export const formTypesController = async <T extends Exact<FormTypesControllerRequest, T>>(data: T) => {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_TYPES_ACTION_PATHS[action]) || "/forms/formTypesController";
  const response = await axiosWrapper.post<FormTypesControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
export const formController = async <T extends Exact<FormControllerRequest, T>>(data: T) => {
  const action = (data as { action?: string }).action;
  const path = (action && FORM_ACTION_PATHS[action]) || "/forms/formController";
  const response = await axiosWrapper.post<FormControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

export const addSectionToLibrary = async <T extends Exact<AddSectionToLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<AddSectionToLibraryResponse>(
    `${getBackendUrl()}/forms/addSectionToLibrary`,
    data,
  );
  return response;
};
export const getEntitesFromLibrary = async <T extends Exact<GetEntitesFromLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetEntitesFromLibraryResponse>(
    `${getBackendUrl()}/forms/getEntitesFromLibrary`,
    data,
  );
  return response;
};

export const deleteSectionsFromLibrary = async <T extends Exact<DeleteSectionsFromLibraryRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<DeleteSectionsFromLibraryResponse>(
    `${getBackendUrl()}/forms/deleteSectionsFromLibrary`,
    data,
  );
  return response;
};
